import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Bin,
  EllipsisMenu,
  IOption,
  MultiSelect,
  PriceBandIcon,
  Prompt,
  SearchField,
  SnackbarStatus,
  Table,
  Typography,
  useSnackbar,
  ViewIcon,
} from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { Range } from '@weareopenr/types';
import { EmptyOrNoResults } from 'src/components/ProductsTab/ProductsTab';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { StatusChip } from 'src/components/StatusChip/StatusChip';
import { OptionGroupType } from 'src/constants';
import { capitalise } from 'src/helpers/capitalise';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useOptionGroups } from 'src/hooks/useOptionGroups';
import { IExternalSystem } from 'src/types/menu.types';
import { OptionGroupOption } from 'src/api/getOptionGroups/types';
import { RangeOption } from 'src/pages/Items/Items';
import { Routes } from 'src/routes';
import Burger from 'src/assets/images/Burger';
import styles from 'src/pages/Items/Items.module.scss';
import { deleteOptionGroup } from '../../api/deleteOptionGroup/deleteOptionGroup';
import { QueryKeys } from '../../hooks/QueryKeys';

type OptionGroupTableRow = {
  option_group_id: string;
  name: string;
  type: OptionGroupType;
  consumer_name: string | null;
  ranges: Range[];
  options: OptionGroupOption[];
  external_systems: IExternalSystem[];
};

const columnHelper = createColumnHelper<OptionGroupTableRow>();

const createTableColumns = (
  onView: (row: OptionGroupTableRow) => void,
  onDelete: (row: OptionGroupTableRow) => void,
) => [
  columnHelper.accessor('name', {
    header: 'Option Group Name',
  }),
  columnHelper.accessor('consumer_name', {
    header: 'Consumer Name',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('ranges', {
    header: 'Range',
    cell: (info) => {
      const ranges = info.getValue() || [];
      return ranges.map(({ name }) => name).join(', ') || '-';
    },
    size: 200,
  }),
  columnHelper.accessor('options', {
    header: 'Products using Option Group',
    cell: (info) => {
      const products = info.getValue() || [];
      return products.length || '-';
    },
    size: 250,
  }),
  columnHelper.accessor('type', {
    header: 'Option Group Type',
    cell: (info) => {
      const type = info.getValue();
      return type ? (
        <StatusChip entity="option-group" status={capitalise(type)} label={`${capitalise(type)} Group`} />
      ) : (
        '-'
      );
    },
    size: 150,
  }),
  columnHelper.display({
    id: 'actions',
    size: 80,
    cell: ({ row }) => {
      const options: IOption[] = [
        { id: 'view', name: 'View', icon: ViewIcon, onOptionClick: () => onView(row.original) },
        {
          id: 'delete',
          name: 'Delete',
          icon: Bin,
          onOptionClick: () => onDelete(row.original),
          hoverColour: 'tomato',
        },
      ];
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <EllipsisMenu options={options} variant="ghost" hoverStyle="high-contrast" />
        </div>
      );
    },
  }),
];

export const OptionGroupsTab = ({
  isSyncInProgress,
  onOpenOptionGroupModal,
  rangeOptions,
}: {
  isSyncInProgress: boolean;
  onOpenOptionGroupModal: () => void;
  rangeOptions: RangeOption[];
}): ReactElement => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchType, setSearchType] = useState<OptionGroupType[]>([]);
  const [searchRanges, setSearchRanges] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOptionGroup, setSelectedOptionGroup] = useState<OptionGroupTableRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (row: OptionGroupTableRow) => {
    setSelectedOptionGroup(row);
    setShowDeleteModal(true);
  };

  const handleDeleteOptionGroup = async () => {
    if (!selectedOptionGroup) return;
    setIsDeleting(true);
    try {
      await deleteOptionGroup(selectedOptionGroup.option_group_id);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.optionGroups] });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Successfully deleted option group ${selectedOptionGroup.name}`,
      });

      setShowDeleteModal(false);
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Something went wrong whilst deleting option group',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const debouncedSearchQuery = useDebounce(searchQuery);
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading: areOptionGroupsLoading,
  } = useOptionGroups({
    name: debouncedSearchQuery,
    type: searchType.length > 0 ? searchType : null,
    ranges: searchRanges.length > 0 ? searchRanges : null,
  });

  const typeOptions = [OptionGroupType.MODIFIER, OptionGroupType.BUNDLE].map((type, index) => ({
    id: type,
    label: `${type[0].toUpperCase()}${type.slice(1).toLowerCase()}`,
    display_order: index,
  }));

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value.toLowerCase());

  const onView = useCallback(
    (row: OptionGroupTableRow) => navigate(`${Routes.OPTION_GROUPS}/${row.option_group_id}`),
    [navigate],
  );

  const tableColumns = useMemo(() => createTableColumns(onView, handleDeleteClick), [onView, handleDeleteClick]);

  const optionGroups = useMemo(() => data?.pages.flatMap((page) => page.data), [data]);

  return (
    <>
      <Prompt
        promptHeading={`Delete ${selectedOptionGroup?.name}?`}
        closePrompt={() => setShowDeleteModal(false)}
        open={showDeleteModal}
        buttonHandler={handleDeleteOptionGroup}
        buttonText="Delete"
        buttonColour="tomato"
        backButtonText="Cancel"
        backButton={true}
        size="small"
        isSubmitting={isDeleting}
      >
        This action will remove this group from all items within your library
      </Prompt>
      <div className={styles['search-container']}>
        <SearchField isDisabled={false} placeholder="Search for an option group" onChange={handleOnChange} />
        {rangeOptions.length > 0 && (
          <MultiSelect
            Icon={PriceBandIcon}
            label="Ranges"
            options={rangeOptions}
            selected={searchRanges}
            onChange={setSearchRanges}
          />
        )}
        <MultiSelect
          Icon={PriceBandIcon}
          label="Types"
          options={typeOptions}
          selected={searchType}
          onChange={(ids: string[]) => setSearchType(ids as OptionGroupType[])}
        />
      </div>

      {areOptionGroupsLoading || isSyncInProgress ? (
        <SkeletonContainer hiddenTitle />
      ) : (
        <>
          {optionGroups && optionGroups.length > 0 ? (
            <main className={styles['results-container']}>
              {debouncedSearchQuery && (
                <div className={styles['search-result']}>
                  <Typography size="large" inline>
                    Search results for{' '}
                  </Typography>
                  <Typography size="large" weight="semi-bold" inline>
                    &quot;{debouncedSearchQuery}&quot;
                  </Typography>
                </div>
              )}
              <Table
                className={styles['option-groups-table']}
                data={optionGroups}
                columns={tableColumns}
                isRowClickable={(row) => (row.option_group_id ? onView : false)}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </main>
          ) : (
            <EmptyOrNoResults
              searchQuery={searchQuery}
              hasActiveFilters={searchType.length > 0 || searchRanges.length > 0}
              emptyStateText="Add your first option group"
              buttonText="New option group"
              Icon={Burger}
              onButtonClick={onOpenOptionGroupModal}
            />
          )}
        </>
      )}
    </>
  );
};
